import { Card } from '../../../molecules/Card/Card';
import {
  isEmpty,
  type ChartRow,
  type ChartBaseProps,
} from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';
import styles from './TopMediaOutletsCard.module.css';

export type TopMediaOutletsView = {
  /** `Most stories` — the top bar's outlet, its story count as the badge. */
  kpis: KPIMetric[];
  /** One row per outlet on the page, ranked by stories; one key per sentiment. */
  rows: ChartRow[];
  xKey: string;
  series: ChartSeries[];
  /** The value axis, scaled over **every outlet**, not the page — one scale across pages. */
  domain: [number, number];
  ticks: number[];
  /** The page shown — the requested one, clamped to `pageCount`. */
  page: number;
  pageCount: number;
  /** Whether any story is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
  /** Comparison-period reading per row and series; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type TopMediaOutletsCardProps = WidgetBlockProps & {
  view?: TopMediaOutletsView;
  onPageChange: (page: number) => void;
  /** Opens the stories behind a bar (index into the page's `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Figma `947:18143` — which publications write about us, and how.
 *
 * Counts, not shares, and ranked by them — so the outlet the KPI names is the
 * top bar and its length is the reason. A normalised plot would draw eight
 * equal bars and put the whole ranking in the sort order, which is the one
 * reading "Top …" cannot afford to lose. Eight bars to a page, the count Figma
 * draws: a live workspace carries twenty and more, and that many bars in the
 * plot draw their names over each other.
 *
 * **Pagination** is in the legend slot, the pager the card's bottom edge with
 * the sentiment key over its empty left side — the layout of
 * `KeywordMentionsFrequencyCard`. Drawn only when there is a second page.
 *
 * Earned media only — the rows arrive already cut to coverage.
 */
export function TopMediaOutletsCard({
  status,
  view,
  span = 12,
  chrome,
  onRetry,
  onPageChange,
  onViewDetails,
}: TopMediaOutletsCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  const shell = cardState(status, hasData, onRetry);
  const paged = view !== undefined && view.pageCount > 1;
  return (
    <Card
      {...chrome}
      /* Flush only while the pager is drawn: it is then the card's bottom edge. */
      className={[spanClass(span), paged && shell.state === 'default' && styles.flush]
        .filter(Boolean)
        .join(' ')}
      emptyLabel="No coverage in this period"
      {...shell}
      title="Top media outlets by sentiment"
      helpText="Media outlets ranked by story count, with each outlet’s coverage split into positive, neutral and negative sentiment."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            stacked
            orientation="horizontal"
            height={PLOT_HEIGHT}
            legend="none"
            /* The value axis, horizontal on this rotated plot. */
            yDomain={view.domain}
            yTicks={view.ticks}
            label={
              paged
                ? `Media outlets by stories, split by sentiment. Page ${view.page} of ${view.pageCount}.`
                : undefined
            }
            formatValue={view.formatValue}
            formatCompare={view.formatCompare}
          />
        )
      }
      legend={
        view &&
        (paged ? (
          <div className={styles.footer}>
            {/* The pager first, so the key paints over its empty left side. */}
            <Pagination
              className={styles.pager}
              page={view.page}
              pageCount={view.pageCount}
              onPageChange={onPageChange}
              label="Media outlet pages"
            />
            <ChartLegend className={styles.key} items={legendItems(view.series)} />
          </div>
        ) : (
          <ChartLegend items={legendItems(view.series)} />
        ))
      }
    />
  );
}
